import React, { useState } from 'react';
import { useStripe } from '@stripe/react-stripe-js';
import { usePaymentsContext } from '../../shared/providers/PaymentsProvider';
import { ERROR_MESSAGES } from '../../shared/constants';
import { validateStripeId } from '../../shared/utils/validation';

interface CheckoutButtonProps {
  priceId: string;
  customerId?: string;
  customerEmail?: string;
  successUrl?: string;
  cancelUrl?: string;
  children: React.ReactNode;
  className?: string;
  disabled?: boolean;
  allowPromotionCodes?: boolean;
  billingAddressCollection?: 'auto' | 'required';
  metadata?: Record<string, string>;
  trialPeriodDays?: number;
  onSuccess?: () => void;
  onError?: (error: string) => void;
  onLoading?: (loading: boolean) => void;
}

export const CheckoutButton: React.FC<CheckoutButtonProps> = ({
  priceId,
  customerId,
  customerEmail,
  successUrl = typeof window !== 'undefined'
    ? `${window.location.origin}/success`
    : '/success',
  cancelUrl = typeof window !== 'undefined'
    ? `${window.location.origin}/cancel`
    : '/cancel',
  children,
  className = '',
  disabled = false,
  allowPromotionCodes = true,
  billingAddressCollection = 'auto',
  metadata,
  trialPeriodDays,
  onSuccess,
  onError,
  onLoading,
}) => {
  const stripe = useStripe();
  const { initialized } = usePaymentsContext();
  const [loading, setLoading] = useState(false);

  const handleCheckout = async () => {
    if (!stripe) {
      const error = ERROR_MESSAGES.STRIPE_NOT_LOADED;
      onError?.(error);
      console.error(error);
      return;
    }

    if (!initialized) {
      const error = ERROR_MESSAGES.PROVIDER_NOT_CONFIGURED;
      onError?.(error);
      console.error(error);
      return;
    }

    if (!validateStripeId(priceId, 'price')) {
      const error = ERROR_MESSAGES.INVALID_PRICE_ID;
      onError?.(error);
      console.error(error);
      return;
    }

    if (customerId && !validateStripeId(customerId, 'customer')) {
      const error = ERROR_MESSAGES.INVALID_CUSTOMER_ID;
      onError?.(error);
      console.error(error);
      return;
    }

    setLoading(true);
    onLoading?.(true);

    try {
      const response = await fetch('/api/payments/create-checkout-session', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          priceId,
          customerId,
          customerEmail,
          successUrl,
          cancelUrl,
          allowPromotionCodes,
          billingAddressCollection,
          metadata,
          trialPeriodDays,
        }),
      });

      if (!response.ok) {
        const errorData = await response
          .json()
          .catch(() => ({ error: 'Network error' }));
        throw new Error(errorData.error || ERROR_MESSAGES.CHECKOUT_FAILED);
      }

      const { sessionId } = await response.json();

      if (!sessionId) {
        throw new Error('No session ID returned from server');
      }

      const result = await stripe.redirectToCheckout({ sessionId });

      if (result.error) {
        throw new Error(result.error.message || ERROR_MESSAGES.CHECKOUT_FAILED);
      }

      onSuccess?.();
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : ERROR_MESSAGES.CHECKOUT_FAILED;
      onError?.(errorMessage);
      console.error('Checkout error:', error);
    } finally {
      setLoading(false);
      onLoading?.(false);
    }
  };

  const isDisabled = !stripe || loading || disabled || !initialized;

  return (
    <button
      onClick={handleCheckout}
      disabled={isDisabled}
      className={`
        inline-flex items-center justify-center px-4 py-2 
        border border-transparent text-sm font-medium rounded-md 
        text-white bg-blue-600 hover:bg-blue-700 
        focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500
        disabled:opacity-50 disabled:cursor-not-allowed
        transition-colors duration-200
        ${className}
      `}
      aria-label={typeof children === 'string' ? children : 'Checkout'}
    >
      {loading ? (
        <>
          <svg
            className="animate-spin -ml-1 mr-3 h-4 w-4 text-white"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            ></circle>
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            ></path>
          </svg>
          Processing...
        </>
      ) : (
        children
      )}
    </button>
  );
};

export default CheckoutButton;
